import { PublicAddress, PublicFieldView, USState } from "client/openapi";

export type AnswerValue =
  | boolean
  | number
  | string
  | Array<number>
  | PublicAddress
  | null;

const inputClass =
  "block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-base text-gray-900 shadow-sm focus:border-green-500 focus:ring-green-500 disabled:bg-gray-50";

const STATES = Object.values(USState);

/** The starting value of a question: what is on file when the form shows it. */
export function initialAnswer(field: PublicFieldView): AnswerValue {
  if (field.input === "subjects") {
    return Array.isArray(field.value) ? (field.value as number[]) : [];
  }
  if (field.input === "address") {
    return (field.value as PublicAddress) || {};
  }
  return (field.value as AnswerValue) ?? null;
}

/**
 * One question on a form. Phone-friendly: 16px text, the phone's own pickers
 * for dates and lists, and plain buttons for Yes or No.
 */
export default function FormFieldInput({
  field,
  value,
  onChange,
  error,
  id,
  disabled,
}: {
  field: PublicFieldView;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
  error?: string;
  id: string;
  disabled?: boolean;
}) {
  const label = (
    <span className="block text-sm font-medium text-gray-800 mb-1.5">
      {field.label}
      {!field.required && (
        <span className="font-normal text-gray-500"> (optional)</span>
      )}
    </span>
  );

  let control: JSX.Element;
  switch (field.input) {
    case "address": {
      const address = (value as PublicAddress) || {};
      const set = (part: keyof PublicAddress, text: string) =>
        onChange({ ...address, [part]: text });
      control = (
        <div className="grid grid-cols-6 gap-2">
          <input
            id={id}
            className={`${inputClass} col-span-6`}
            placeholder="Street"
            autoComplete="address-line1"
            value={address.line1 || ""}
            onChange={(e) => set("line1", e.target.value)}
            disabled={disabled}
          />
          <input
            className={`${inputClass} col-span-6`}
            placeholder="Apt or unit"
            autoComplete="address-line2"
            value={address.line2 || ""}
            onChange={(e) => set("line2", e.target.value)}
            disabled={disabled}
          />
          <input
            className={`${inputClass} col-span-6 sm:col-span-3`}
            placeholder="City"
            autoComplete="address-level2"
            value={address.city || ""}
            onChange={(e) => set("city", e.target.value)}
            disabled={disabled}
          />
          <select
            className={`${inputClass} col-span-3 sm:col-span-1`}
            autoComplete="address-level1"
            value={address.state || ""}
            onChange={(e) => set("state", e.target.value)}
            disabled={disabled}
            aria-label="State"
          >
            <option value="">State</option>
            {STATES.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          <input
            className={`${inputClass} col-span-3 sm:col-span-2`}
            placeholder="ZIP"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={5}
            value={address.zip || ""}
            onChange={(e) => set("zip", e.target.value)}
            disabled={disabled}
          />
        </div>
      );
      break;
    }
    case "boolean":
      control = (
        <div
          className="flex gap-2"
          role="group"
          aria-labelledby={id + "-label"}
        >
          {[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ].map((option) => (
            <button
              key={option.label}
              type="button"
              className={`flex-1 rounded-lg border px-3 py-2.5 text-base ${
                value === option.value
                  ? "border-green-500 bg-green-50 text-green-800 font-medium"
                  : "border-gray-300 bg-white text-gray-800"
              }`}
              onClick={() =>
                onChange(value === option.value ? null : option.value)
              }
              disabled={disabled}
              aria-pressed={value === option.value}
            >
              {option.label}
            </button>
          ))}
        </div>
      );
      break;
    case "choice":
      control = (
        <select
          id={id}
          className={inputClass}
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) =>
            onChange(e.target.value === "" ? null : Number(e.target.value))
          }
          disabled={disabled}
        >
          <option value="">Select...</option>
          {(field.options || []).map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      );
      break;
    case "subjects": {
      const chosen = (value as number[]) || [];
      control = (
        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-labelledby={id + "-label"}
        >
          {(field.options || []).map((option) => {
            const on = chosen.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                className={`rounded-full border px-3 py-1.5 text-sm ${
                  on
                    ? "border-green-500 bg-green-50 text-green-800 font-medium"
                    : "border-gray-300 bg-white text-gray-700"
                }`}
                onClick={() =>
                  onChange(
                    on
                      ? chosen.filter((v) => v !== option.id)
                      : [...chosen, option.id]
                  )
                }
                disabled={disabled}
                aria-pressed={on}
              >
                {option.name}
              </button>
            );
          })}
        </div>
      );
      break;
    }
    case "number":
      control = (
        <input
          id={id}
          type="number"
          step="any"
          inputMode="decimal"
          className={inputClass}
          min={
            typeof field.min_value === "number" ? field.min_value : undefined
          }
          max={
            typeof field.max_value === "number" ? field.max_value : undefined
          }
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) =>
            onChange(e.target.value === "" ? null : Number(e.target.value))
          }
          disabled={disabled}
        />
      );
      break;
    case "date":
      control = (
        <input
          id={id}
          type="date"
          className={inputClass}
          min={
            typeof field.min_value === "string" ? field.min_value : undefined
          }
          max={
            typeof field.max_value === "string" ? field.max_value : undefined
          }
          autoComplete={field.field === "birth_date" ? "bday" : undefined}
          value={typeof value === "string" ? value.slice(0, 10) : ""}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
        />
      );
      break;
    case "textarea":
      // A note question: the answer is kept on the response and noted.
      control = (
        <textarea
          id={id}
          className={`${inputClass} resize-y`}
          rows={4}
          maxLength={5000}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );
      break;
    default: {
      const typeByField: Record<string, string> = {
        email: "email",
        tel: "tel",
      };
      const autoComplete: Record<string, string> = {
        first_name: "given-name",
        last_name: "family-name",
        email: "email",
        phone_number: "tel",
      };
      control = (
        <input
          id={id}
          type={typeByField[field.input] || "text"}
          inputMode={
            field.input === "tel"
              ? "tel"
              : field.input === "email"
              ? "email"
              : undefined
          }
          className={inputClass}
          autoComplete={autoComplete[field.field]}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );
    }
  }

  return (
    <div className="mb-4">
      <label htmlFor={id} id={id + "-label"}>
        {label}
      </label>
      {/* The form's directions for this question. */}
      {field.prompt && (
        <p className="-mt-1 mb-1.5 text-xs text-gray-500 whitespace-pre-line">
          {field.prompt}
        </p>
      )}
      {control}
      {error && (
        <p className="mt-1 text-sm text-red-600" data-form-error>
          {error}
        </p>
      )}
    </div>
  );
}
